import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Pencil, Save, Trash2 } from "lucide-react";
import { usDate } from "./ui";

const bc = { borderColor: "var(--ll-border)" };
const small = "text-xs px-2.5 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 transition-colors disabled:opacity-40";

// Order-insensitive comparison ignoring empty values.
const norm = (v) => {
  if (Array.isArray(v)) return v.map(norm);
  if (v && typeof v === "object") {
    return Object.keys(v).sort().reduce((o, k) => {
      if (v[k] !== null && v[k] !== undefined && v[k] !== "") o[k] = norm(v[k]);
      return o;
    }, {});
  }
  return typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)) ? Number(v) : v;
};
const canon = (v) => JSON.stringify(norm(v || {}));

const errText = (err) => {
  const d = err?.response?.data?.detail;
  return Array.isArray(d) ? d[0]?.msg : d || "Something went wrong";
};

export default function ScenarioBar({ current, onSwitched }) {
  const dirty = (s) => s && canon(s.forecast) !== canon(current);
  const [data, setData] = useState({ items: [], active_id: null });
  const load = async () => {
    try { setData((await api.get("/advisory/scenarios")).data); } catch { /* staff only */ }
  };
  useEffect(() => { load(); }, []);
  const active = data.items.find((s) => s.id === data.active_id);

  const run = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); } catch (err) { toast.error(errText(err)); }
  };
  const switchTo = (id) => {
    if (active ? dirty(active) : data.items.length > 0) {
      if (!window.confirm("You have unsaved changes to the current assumptions. Switch anyway and discard them?")) return;
    }
    doSwitch(id);
  };
  const doSwitch = (id) => run(async () => {
    await api.post(`/advisory/scenarios/${id}/activate`);
    onSwitched();
  }, "Scenario loaded");
  const saveAs = () => {
    const name = window.prompt("Name this scenario (e.g. Hire 2 in Q1):");
    if (!name?.trim()) return;
    run(async () => {
      await api.post("/advisory/scenarios", { name: name.trim(), forecast: current });
      onSwitched();
    }, `Saved "${name.trim()}"`);
  };
  const saveActive = () => run(async () => {
    await api.patch(`/advisory/scenarios/${active.id}`, { forecast: current });
    await load();
  }, `Updated "${active.name}"`);
  const rename = () => {
    const name = window.prompt("Rename scenario:", active.name);
    if (!name?.trim() || name.trim() === active.name) return;
    run(async () => { await api.patch(`/advisory/scenarios/${active.id}`, { name: name.trim() }); await load(); }, "Renamed");
  };
  const del = () => {
    if (!window.confirm(`Delete scenario "${active.name}"? Current assumptions stay loaded.`)) return;
    run(async () => { await api.delete(`/advisory/scenarios/${active.id}`); await load(); }, "Scenario deleted");
  };

  return (
    <div className="mt-6 pt-5 border-t flex flex-wrap items-center gap-2" style={bc} data-testid="scenario-bar">
      <Layers size={15} className="text-[var(--ll-ink-muted)]" />
      <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mr-1">Scenario</span>
      <select
        value={data.active_id || ""}
        onChange={(e) => e.target.value && switchTo(e.target.value)}
        className="px-2 py-1.5 border bg-white text-sm min-w-[200px]"
        style={bc}
        data-testid="scenario-select"
      >
        <option value="">{data.items.length ? "Unsaved assumptions" : "No saved scenarios"}</option>
        {data.items.map((s) => <option key={s.id} value={s.id}>{s.name}{s.id === data.active_id ? " (active)" : ""}</option>)}
      </select>
      {!active && data.items.length > 0 && (
        <span className="text-[10px] text-[var(--ll-ink-muted)]" data-testid="scenario-unsaved-hint">Current assumptions aren't saved as a scenario — use Save as… to keep them</span>
      )}
      {active && (
        <>
          {dirty(active) && <span className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]" data-testid="scenario-dirty">Unsaved changes</span>}
          <button onClick={saveActive} className={small} style={bc} data-testid="scenario-update-btn"><Save size={12} /> Update</button>
          <button onClick={rename} className={small} style={bc} data-testid="scenario-rename-btn"><Pencil size={12} /> Rename</button>
          <button onClick={del} className={small} style={bc} data-testid="scenario-delete-btn"><Trash2 size={12} /> Delete</button>
          <span className="text-[10px] text-[var(--ll-ink-muted)]" data-testid="scenario-meta">Updated {usDate(active.updated_at)} · used for debt plan & goals</span>
        </>
      )}
      <button onClick={saveAs} className={`${small} ml-auto`} style={bc} data-testid="scenario-save-as-btn"><Save size={12} /> Save as…</button>
    </div>
  );
}
